import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Button, Col, DatePicker, Form, InputNumber, Modal, Popconfirm, Row, Select, Space, Table, Tag } from 'antd';
import { CheckOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import PageTitle from '@/components/PageTitle';
import TableActionBar from '@/components/TableActionBar';
import { checkPermission } from '@/utils/permission';
import { mapUserOptionLabels } from '@/utils/userOptionHelpers';
import { fetchCommissionPolicies } from '@/redux/actions/commissionPolicyActions';
import { approveCommissionPayoutSchedule, createCommissionPayoutSchedule, fetchCommissionPayoutSchedules } from '@/redux/actions/commissionActions';
import { isMobile } from 'react-device-detect';

export default function CommissionPayoutSchedulePage({ embedded = false }) {
    const dispatch = useDispatch();
    const capabilities = useSelector(state => state.commissionPolicyReducer.capabilities || {});
    const users = useSelector(state => state.config.users || []);
    const canCreate = checkPermission('commission_payout_schedule', 'configure');
    const canApprove = checkPermission('commission_payout_schedule', 'approve');
    const [rows, setRows] = useState([]);
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [, schedules] = await Promise.all([dispatch(fetchCommissionPolicies({ per_page: 1 })), dispatch(fetchCommissionPayoutSchedules())]);
            setRows(schedules);
        } catch (error) { /* Action đã hiển thị lỗi. */ }
        finally { setLoading(false); }
    }, [dispatch]);
    useEffect(() => { load(); }, [load]);
    const create = async () => {
        const values = await form.validateFields();
        await dispatch(createCommissionPayoutSchedule({ ...values, effective_from: values.effective_from.format('YYYY-MM-DD HH:mm:ss'), effective_to: values.effective_to?.format('YYYY-MM-DD HH:mm:ss') }));
        setVisible(false); form.resetFields(); load();
    };
    const approve = async id => { await dispatch(approveCommissionPayoutSchedule(id)); await load(); };
    const catalogs = capabilities.catalogs || {};
    const employeeOptions = useMemo(
        () => mapUserOptionLabels(catalogs.employees || [], users),
        [catalogs.employees, users]
    );
    const columns = [
        { title: 'Đối tượng áp dụng', dataIndex: 'employee_id', width: 210, render: value => employeeOptions.find(item => Number(item.value) === Number(value))?.label || 'Tất cả nhân viên' },
        { title: 'Vai trò', dataIndex: 'role_key', width: 170, render: value => catalogs.role_keys?.find(item => item.value === value)?.label || 'Tất cả' },
        { title: 'Cơ sở', dataIndex: 'branch_id', width: 180, render: value => catalogs.branches?.find(item => Number(item.value) === Number(value))?.label || 'Tất cả' },
        { title: 'Thời điểm chi trả', dataIndex: 'delay_months', width: 190, render: value => Number(value) === 0 ? 'Trong kỳ ghi nhận' : `Sau ${value} tháng` },
        {
            title: 'Áp dụng từ',
            dataIndex: 'effective_from',
            width: 190,
            render: (value, row) => (
                <div>
                    <div>{moment(value).format('DD/MM/YYYY HH:mm')}</div>
                    <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
                        Đến: {row.effective_to ? moment(row.effective_to).format('DD/MM/YYYY HH:mm') : 'Không giới hạn'}
                    </div>
                </div>
            )
        },
        {
            title: 'Trạng thái', dataIndex: 'status', width: 150, render: (value, row) => {
                if (value === 'DRAFT') return <Tag>Chờ duyệt</Tag>;
                if (value === 'RETIRED' || (row.effective_to && moment(row.effective_to).isSameOrBefore(moment()))) return <Tag>Hết hiệu lực</Tag>;
                if (row.effective_from && moment(row.effective_from).isAfter(moment())) return <Tag color="processing">Đã duyệt - Chờ hiệu lực</Tag>;
                return <Tag color="success">Đang áp dụng</Tag>;
            }
        },
        {
            title: 'Thao tác', key: 'action', fixed: 'right', width: 100, render: (_, row) => {
                if (!canApprove || row.status !== 'DRAFT') return '—';
                return <Popconfirm title="Duyệt và kích hoạt lịch chi trả này?" onConfirm={() => approve(row.id)}><Button size="small" type="primary" icon={<CheckOutlined />}>Duyệt</Button></Popconfirm>;
            }
        },
    ];
    return <div>
        {!embedded && <PageTitle routes={[{ path: '/', breadcrumbName: 'Trang chủ' }, { path: '#', breadcrumbName: 'Lịch chi trả' }]} title="Cấu hình lịch chi trả hoa hồng" />}
        <TableActionBar
            isShowAddButton={false}
            isShowDeleteButton={false}
            showSearch={false}
            showFilter={false}>
            <Space>
                {canCreate && <Button type="primary" icon={<PlusOutlined />} onClick={() => { setVisible(true); form.setFieldsValue({ delay_months: 0, effective_from: moment() }); }}>Tạo lịch</Button>}
                <Button icon={<ReloadOutlined />} loading={loading} onClick={load}>Làm mới</Button>
            </Space>
        </TableActionBar>
        <Alert
            type="info"
            className="mb-3 mt-3"
            message="Cách hệ thống áp dụng lịch chi trả"
            description={
                <span>
                    Lịch chi trả quyết định thời điểm một khoản hoa hồng đã duyệt và đã chốt kỳ được đưa vào <b>Danh sách chi trả</b>.
                    Lịch đang áp dụng được kiểm tra tại thời điểm lập danh sách, vì vậy lịch mới được duyệt cũng có thể áp dụng cho các khoản cũ chưa chi nếu đúng nhân viên, vai trò hoặc cơ sở.
                    Khoản chưa có lịch phù hợp hoặc chưa đến hạn sẽ chưa được đưa vào danh sách. Lịch đã duyệt không sửa trực tiếp; khi cần thay đổi hãy tạo lịch mới với thời điểm hiệu lực phù hợp.
                </span>
            }
        />
        <div className="mt-2">
            <Table size="small" rowKey="id" loading={loading} dataSource={rows} columns={columns} scroll={{ x: 'max-content' }} />
        </div>
        <Modal
            visible={visible}
            title="Thiết lập thời điểm chi trả"
            okText="Lưu để chờ duyệt"
            cancelText="Hủy"
            width={isMobile ? '100%' : "50%"}
            onCancel={() => setVisible(false)}
            onOk={create}
        >
            <Form form={form} layout="vertical">
                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Form.Item name="employee_id" label="Nhân viên">
                            <Select
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                placeholder="Chọn nhân viên"
                                style={{ width: '100%' }}
                                options={employeeOptions}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item name="role_key" label="Vai trò">
                            <Select
                                allowClear
                                placeholder="Chọn vai trò"
                                style={{ width: '100%' }}
                                options={catalogs.role_keys || []}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item name="branch_id" label="Cơ sở">
                            <Select
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                placeholder="Chọn cơ sở"
                                style={{ width: '100%' }}
                                options={catalogs.branches || []}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item
                            name="delay_months"
                            label="Chi trả sau kỳ ghi nhận (tháng)"
                            extra="Nhập 0 nếu chi trả ngay trong kỳ ghi nhận."
                            rules={[
                                {
                                    required: true,
                                    message: 'Nhập số tháng chờ trước khi chi trả.',
                                },
                            ]}
                        >
                            <InputNumber
                                min={0}
                                max={120}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item
                            name="effective_from"
                            label="Hiệu lực từ"
                            rules={[{ required: true }]}
                        >
                            <DatePicker
                                showTime
                                format="DD/MM/YYYY HH:mm"
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item
                            name="effective_to"
                            label="Hiệu lực đến"
                        >
                            <DatePicker
                                showTime
                                format="DD/MM/YYYY HH:mm"
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    </div>;
}
